import type { HTMLAttributes } from 'react';
import { cn } from './cn';
import { accent, accentOrder } from './accent';

/**
 * Shimmer placeholder. Prefer these over spinners for content that has a known
 * shape — the page keeps its layout and nothing jumps when data lands.
 */
export const Skeleton = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    aria-hidden="true"
    className={cn('relative overflow-hidden rounded-md bg-background-alt', className)}
    {...props}
  >
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-border/50 to-transparent" />
  </div>
);

/**
 * Matches the CourseCard footprint so grids don't reflow on load.
 *
 * The media area is tinted rather than grey. A wall of grey rectangles is the
 * single greyest moment on the site and it is the *first* thing a visitor sees;
 * cycling the accent by index makes a loading grid look like the colourful grid
 * that is about to replace it.
 */
export const CardSkeleton = ({ index = 0 }: { index?: number }) => {
  const palette = accent(accentOrder[index % accentOrder.length]);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background-card">
      <div className={cn('relative aspect-[3/2] overflow-hidden', palette.soft)}>
        {/* The same rising-bars motif as the empty state and the logo. */}
        <div aria-hidden="true" className="absolute inset-0 flex items-end justify-center gap-2 px-10 pb-8 opacity-30">
          {[40, 62, 48, 78].map((height) => (
            <span key={height} className={cn('w-4 rounded-t-sm', palette.rule)} style={{ height: `${height}%` }} />
          ))}
        </div>
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent" />
      </div>
      <div className="space-y-3 p-5">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-2/3" />
        <div className="flex gap-3 pt-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    </div>
  );
};

export default Skeleton;
